import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { romaneioService } from '../../../../../api';
import { useOps } from '../../../../../hooks/useOps';
import {
  AllStatisticsData,
  STATISTICS_TYPES,
  StatisticsQueryParams,
} from '../types';
import { extractDataArray, transformDataForChart } from '../utils';

export const statisticsQueryKeys = {
  all: ['romaneio-statistics'] as const,
  byDate: (filterDate: string, ops: string) =>
    [...statisticsQueryKeys.all, filterDate, ops] as const,
};

async function fetchAllStatistics(
  params: StatisticsQueryParams
): Promise<AllStatisticsData> {
  const results = await Promise.all(
    STATISTICS_TYPES.map((type) =>
      romaneioService
        .getStatistics({
          filterDate: params.filterDate,
          type,
          dbName: params.dbName,
          ops: params.ops,
        })
        .catch(() => null)
    )
  );

  const [antecipacaoDia, ticketMedioDia, totalAntecipadoDia, aderencia, cadAprovadosDia] =
    results;

  return {
    antecipacaoDia: transformDataForChart(
      extractDataArray(antecipacaoDia),
      'n_antecipacoes'
    ),
    ticketMedioDia: transformDataForChart(
      extractDataArray(ticketMedioDia),
      'ticker_medio'
    ),
    totalAntecipadoDia: transformDataForChart(
      extractDataArray(totalAntecipadoDia),
      'valor_antecipado_compra'
    ),
    aderencia: transformDataForChart(extractDataArray(aderencia), 'aderencia'),
    cadAprovadosDia: transformDataForChart(
      extractDataArray(cadAprovadosDia),
      'cadastros_aprovados',
      'data_aprovacao'
    ),
  };
}

const EMPTY_STATISTICS: AllStatisticsData = {
  antecipacaoDia: [],
  ticketMedioDia: [],
  totalAntecipadoDia: [],
  aderencia: [],
  cadAprovadosDia: [],
};

export function useStatistics(filterDate: string) {
  const { currentOps, getCurrentOpsConfig } = useOps();
  const opsConfig = getCurrentOpsConfig();
  const dbName = opsConfig?.dbName;

  const enabled = Boolean(currentOps && dbName);

  const query = useQuery({
    queryKey: statisticsQueryKeys.byDate(filterDate, currentOps || ''),
    queryFn: () =>
      fetchAllStatistics({
        filterDate,
        dbName: dbName!,
        ops: currentOps!,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const hasData = useMemo(() => {
    if (!query.data) {
      return false;
    }
    return Object.values(query.data).some((arr) => arr.length > 0);
  }, [query.data]);

  return {
    data: query.data ?? EMPTY_STATISTICS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasData,
    refetch: query.refetch,
    enabled,
  };
}
